import { PayloadAction }   from '@reduxjs/toolkit';
import { IFilterSettings } from 'Domain/Repositories/BaseRepository/BaseRepository';

export type TOrderSettings<TModel extends  { id: string }> = {
    by: keyof TModel;
    order: string;
};

export interface IFilterProps<TModel extends { id: string }> {
    limit?: number,
    sorting?: TOrderSettings<TModel>,
}

export type TObjectStore<TModel extends  { id: string }> = Record<string, TModel>;


export const queryFilter = <TModel extends  { id: string }>(props: IFilterProps<TModel>): IFilterProps<TModel>|undefined => {
    let filter: IFilterProps<TModel>|undefined;

    if(props.hasOwnProperty('limit')) {
        if(!filter) { filter = {}; }
        filter.limit = props.limit;
    }
    if(props.hasOwnProperty('sorting')) {
        if(!filter) { filter = {}; }
        filter.sorting = props.sorting;
    }

    return filter;
};

export const sortObjects = <TModel extends  { id: string }>(objectStore: TObjectStore<TModel>, orderSettings: TOrderSettings<TModel>): TModel[] => {
    const orderBy        = orderSettings.by || 'id';
    const orderDirection = ((orderSettings.order || 'ASC') === 'ASC') ? 1 : -1;

    return Object.keys(objectStore).map(key => objectStore[key]).sort(( item1, item2 ) => {
        if (item1[orderBy]  < item2[orderBy]) {
            return -orderDirection;
        }
        if (item1[orderBy] > item2[orderBy]) {
            return orderDirection;
        }
        return 0;
    });

};

const filterCache = new WeakMap();


export const filterObjectStore = <TModel extends  { id: string }>(objectStore: TObjectStore<TModel>, filter:IFilterProps<TModel>|null = null): TModel[] => {
  if (!objectStore) {
    return [];
  }

  if (filterCache.has(objectStore)) {
    const cachedResult = filterCache.get(objectStore);
    if (JSON.stringify(cachedResult.filter) === JSON.stringify(filter)) {
      return cachedResult.result;
    }
  }

  let result;

  if (filter === null) {
    result = Object.keys(objectStore).map(key => objectStore[key]);
  }
  else if (filter.hasOwnProperty('limit') && filter.sorting) {
    result = sortObjects(objectStore, filter.sorting).slice(0, filter.limit);
  }
  else if (filter.hasOwnProperty('limit')) {
    result = Object.keys(objectStore).map(key => objectStore[key]).slice(0, filter.limit);
  }
  else if (filter.sorting) {
    result = sortObjects(objectStore, filter.sorting);
  }
  else {
    result = Object.keys(objectStore).map(key => objectStore[key]);
  }
  filterCache.set(objectStore, { filter, result });

  return result;
};

export type PayloadActionList = PayloadAction<IFilterSettings>
